import React, { FormEvent, useState, useEffect } from 'react';
import styled from 'App/styled';
import { NewsletterParams } from 'shared/store/footer/action';
import {
  SUCCESS_MESSAGE,
  FAILURE_MESSAGE,
  BUTTON_TEXT,
  ACC_ERROR_MESSAGE,
} from 'store/footer/constants';
import { YOUR_EMAIL } from 'shared/constants/placeHolders';
import Snackbar from 'components/Snackbar';
import { validateEmail } from 'shared/helpers/utils/validation';
import Envelope from 'components/Icons/Envelope';
import { checkIsIndianUser } from 'helpers/utils/user';
import { Region } from 'types';

const SNACKBAR_ID = 'send-newsletter-prompt';
const INVALID_EMAIL_MESSAGE = 'Please enter valid email';

const NewsletterWrap = styled.div`
  background-color: ${(props) => props.theme.newsletterBak};
  color: #ffffff;
  padding: 0 30px 0;
  line-height: 20px;
  .error-message {
    ${({ theme }) => theme.typography.bodyMedium};
    width: 100%;
    color: red;
  }
`;

const NewsletterTitle = styled.div`
  ${({ theme }) => theme.typography.subTitleMedium};
  i {
    vertical-align: middle;
    margin-right: 10px;
  }
`;

const Form = styled.form`
  margin-top: 10px;

  .input {
    color: #fff;
    background-color: transparent;
    border: none;
    border-bottom: 1px solid #fff;
    width: 72%;
    outline: none;
    padding: 8px 5px;
    margin-right: 3%;
    ${({ theme }) => theme.typography.bodyMedium};

    ::placeholder {
      color: #fff;
      font-weight: 600;
    }

    &:focus {
      outline: none;
    }
  }

  .submit-button {
    text-transform: uppercase;
    background-color: transparent;
    border: 1px solid #fff;
    padding: 6px 12px;
    border-radius: 2px;
    color: #fff;
    ${({ theme }) => theme.typography.type142};
  }
`;
const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

interface NewsletterProps {
  sendNewsletter: (params: NewsletterParams) => Promise;
  userEmail: string;
  newsletterEnabled: boolean;
  region: Region;
}

function Newsletter({
  sendNewsletter,
  userEmail,
  newsletterEnabled,
  region,
}: NewsletterProps) {
  const [email, setEmail] = useState('');
  const [showNewsletterMessage, setNewsletterMessage] = useState(false);
  const [newsletterError, setNewsletterError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [srErrorMessage, setSrErrorMessage] = useState('');
  const isIndianUser = checkIsIndianUser(region?.regionHeaderCountry);

  useEffect(() => {
    setEmail(userEmail);
  }, [userEmail]);
  const handleFormSubmit = (formSubmitEvent: FormEvent) => {
    formSubmitEvent.preventDefault();
    const validEmail = validateEmail(email);
    if (validEmail) {
      sendNewsletter({
        email,
      }).then((res: boolean) => {
        setNewsletterMessage(true);
        setNewsletterError(!res);
        setEmail('');
        setSrErrorMessage('');
      });
    } else {
      setErrorMessage(INVALID_EMAIL_MESSAGE);
      setSrErrorMessage(ACC_ERROR_MESSAGE);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setEmail(value);
    setErrorMessage('');
  };

  if (!newsletterEnabled) return null;
  if (!isIndianUser) return null;

  return (
    <NewsletterWrap>
      <NewsletterTitle>
        <i aria-hidden="true">
          <Envelope />
        </i>
        Get special discount on your inbox
      </NewsletterTitle>
      <Form onSubmit={handleFormSubmit}>
        <input
          type="email"
          className="input"
          placeholder={YOUR_EMAIL}
          value={email}
          onChange={handleChange}
          {...(errorMessage && { 'aria-describedby': 'newsletter-error-sr' })}
          aria-required="true"
          aria-invalid={!!errorMessage}
        />
        <button
          type="submit"
          className="submit-button"
        >
          {BUTTON_TEXT}
        </button>
      </Form>
      {<span className="error-message">{errorMessage}</span>}
      <VisuallyHidden
        id="newsletter-error-sr"
        role="alert"
        aria-live="assertive"
      >
        {srErrorMessage}
      </VisuallyHidden>
      {showNewsletterMessage && (
        <Snackbar
          autoClose
          closeCallback={() => setNewsletterMessage(false)}
          content={newsletterError ? FAILURE_MESSAGE : SUCCESS_MESSAGE}
          errorType={newsletterError}
          snackBarId={SNACKBAR_ID}
        />
      )}
    </NewsletterWrap>
  );
}

export default Newsletter;
